import type { RoleContext } from './roles';
import { roleIndicatesSupervisorNurse } from './roles';

export type AppHomePath = '/kpis' | '/report';

const roleIndicatesHeadWardNurse = (role: string | undefined | null): boolean => {
  if (role == null || typeof role !== 'string') return false;
  const normalized = role.trim().toLowerCase().replace(/[-\s]+/g, '_');
  return normalized === 'hwn' || normalized === 'head_ward_nurse' || normalized.includes('head_ward');
};

/**
 * Post-login landing route for the app shell (must match `HomePage` redirect logic).
 */
export function resolveAppHomePath(roleContext: RoleContext | null): AppHomePath {
  if (!roleContext) return '/report';

  const roles: string[] = [];
  const push = (r?: unknown) => {
    if (typeof r === 'string') {
      const n = r.trim().toLowerCase();
      if (n) roles.push(n);
    }
  };
  push(roleContext.generalRole);
  (roleContext.roles ?? []).forEach(push);
  push(roleContext.positionRole);
  push(roleContext.departmentRole);
  (roleContext.departmentRoles ?? []).forEach((d) => push(typeof d === 'string' ? d : (d as { role?: string }).role));
  push(roleContext.teamRole);
  (roleContext.teamRoles ?? []).forEach(push);
  const roleIdRaw = roleContext.roleId ?? '';
  const roleId = roleIdRaw.toLowerCase();
  const landsOnKpisByGlobalRole = roles.some((r) => r === 'admin' || r === 'manager' || r === 'auditor') ||
    roleId === 'auditor';
  if (landsOnKpisByGlobalRole) return '/kpis';

  if (roles.some(roleIndicatesHeadWardNurse) || roleIndicatesHeadWardNurse(roleIdRaw)) {
    return '/report';
  }

  /** `SN` / legacy tokens do not contain substring "supervisor" — align with `roleIndicatesSupervisorNurse` (roles.ts). */
  const landsOnKpis =
    roles.some((r) => {
      if (roleIndicatesSupervisorNurse(r)) return true;
      return (
        r.includes('manager') ||
        r.includes('auditor') ||
        r.includes('supervisor') ||
        /** legacy substring gate kept from original HomePage */
        r.includes('audit')
      );
    }) ||
    roleIndicatesSupervisorNurse(roleIdRaw);

  return landsOnKpis ? '/kpis' : '/report';
}

/** Compare route paths for “same page” (trailing slash, hosting quirks). */
export function appPathsAreSame(a: string, b: string): boolean {
  const na = a.length > 1 ? a.replace(/\/+$/, '') : a;
  const nb = b.length > 1 ? b.replace(/\/+$/, '') : b;
  return na === nb;
}
